import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PprItemLayout } from "../../../shared/ppr-item-layout/ppr-item-layout";

import { Flex } from "antd";
import PprChipStatus, {
  ChipStatus,
} from "../../../shared/ppr-chip-status/ppr-chip-status";
import "./screen-step.less";
import { sleep } from "../../../util/util";
import {
  checkMultipleMonitor,
  MultipleMonitorCheckResult,
} from "./util/multiple-monitor-check";
import {
  CheckResult,
  getScreenResolution,
} from "./util/screen-resolution-check";

export type ScreenResolutionInfo = {
  width: number;
  height: number;
};

export type ScreenResolutionOptions = {
  minScreenSolution: ScreenResolutionInfo;
};

const DEFAULT_SCREEN_RESOLUTION: ScreenResolutionInfo = {
  width: 1024,
  height: 768,
};

interface ScreenProps {
  screenResolutionCheck: boolean;
  multipleMonitorCheck: boolean;
  retry: number;
  minScreenSolution?: ScreenResolutionInfo;
  onComplete: () => void;
  onFail: (
    errors: string[],
    title?: string,
    showFAQLink?: boolean,
    canRetry?: boolean,
  ) => void;
}

const Screen = ({
  screenResolutionCheck,
  multipleMonitorCheck,
  onComplete,
  onFail,
  retry,
  minScreenSolution = DEFAULT_SCREEN_RESOLUTION,
}: ScreenProps) => {
  const getWindowDimensions = () => ({
    width: window.screen.width,
    height: window.screen.height,
  });
  const [isDone, setIsDone] = useState(false);
  const { t } = useTranslation();

  const [screenResolutionCheckStatus, setScreenResolutionCheckStatus] =
    useState<ChipStatus>("not-started");
  const [multipleMonitorCheckStatus, setMultipleMonitorCheckStatus] =
    useState<ChipStatus>("not-started");
  const [multipleMonitorCheckResult, setMultipleMonitorCheckResult] =
    useState<MultipleMonitorCheckResult>();
  const [screenResolutionInfo, setScreenResolutionInfo] =
    useState<CheckResult>();

  const screenCheck = async (
    screenResolutionCheck: boolean,
    multipleMonitorCheck: boolean,
  ) => {
    if (screenResolutionCheck) {
      setScreenResolutionCheckStatus("analyzing");
      await sleep();
      const { width, height } = getWindowDimensions();
      const result = await getScreenResolution(
        width,
        height,
        minScreenSolution,
      );

      setScreenResolutionInfo(result);
      setScreenResolutionCheckStatus(result.status ? "passed" : "failed");
    }
    if (multipleMonitorCheck) {
      setMultipleMonitorCheckStatus("analyzing");
      await sleep();
      const result = await checkMultipleMonitor();
      setMultipleMonitorCheckResult(result);
      setMultipleMonitorCheckStatus(result.status ? "passed" : "failed");
    }
  };

  const getScreenResolutionDesc = () => {
    switch (screenResolutionCheckStatus) {
      case "not-started":
        return t("SCREEN_CHECK.NOT_STARTED");
      case "analyzing":
        return t("SCREEN_CHECK.ANALYZING_TEXT");
      case "passed":
      case "failed":
        return t(`SCREEN_CHECK.DESCRIPTION`, {screenResolution: screenResolutionInfo?.info});
      default:
        return undefined;
    }
  };

  const getMultipleMonitorDesc = () => {
    switch (multipleMonitorCheckStatus) {
      case "not-started":
        return t("MULTIPLE_SCREEN.NOT_STARTED");
      case "analyzing":
        return t("MULTIPLE_SCREEN.ANALYZING_TEXT");
      case "passed":
        return t("MULTIPLE_SCREEN.PASS");
      case "failed":
        return t(
          multipleMonitorCheckResult?.error === "unsupported"
            ? "MULTIPLE_SCREEN.PASS"
            : "MULTIPLE_SCREEN.FAIL",
        );
      default:
        return undefined;
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    setScreenResolutionCheckStatus("not-started");
    setMultipleMonitorCheckStatus("not-started");
    timeout = setTimeout(() => {
      screenCheck(screenResolutionCheck, multipleMonitorCheck);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [retry]);

  useEffect(() => {
    if (
      (!screenResolutionCheck ||
        ["passed", "failed"].includes(screenResolutionCheckStatus)) &&
      (!multipleMonitorCheck ||
        ["passed", "failed"].includes(multipleMonitorCheckStatus))
    ) {
      setIsDone(true);
    }
  }, [screenResolutionCheckStatus, multipleMonitorCheckStatus]);

  useEffect(() => {
    if (!isDone) return;

    if (
      (!screenResolutionCheck || screenResolutionCheckStatus === "passed") &&
      (!multipleMonitorCheck || multipleMonitorCheckStatus === "passed")
    ) {
      onComplete();
    } else {
      setIsDone(false);
      const errors = [];

      const screenFailed = screenResolutionCheckStatus === "failed";
      const monitorFailed = multipleMonitorCheckStatus === "failed";

      if (screenFailed || monitorFailed) {
        if (screenFailed) {
          errors.push(`SCREEN_CHECK.ERROR`);
        }

        if (monitorFailed) {
          errors.push(
            multipleMonitorCheckResult?.error === "unsupported"
              ? "MULTIPLE_SCREEN.ERROR_UNSUPPORTED"
              : "MULTIPLE_SCREEN.ERROR_FAILED",
          );
        }

        const showFAQLink = screenFailed;
        onFail([...errors], "", showFAQLink, true);
      }
    }
  }, [isDone]);

  return (
    <Flex className="flex-layout" vertical gap={45}>
      {screenResolutionCheck && (
        <PprItemLayout
          title={t("SCREEN_CHECK.TITLE")}
          desc={getScreenResolutionDesc()}
          child={<PprChipStatus status={screenResolutionCheckStatus} />}
        />
      )}
      {multipleMonitorCheck && (
        <PprItemLayout
          title={t(`MULTIPLE_SCREEN.TITLE`)}
          desc={getMultipleMonitorDesc()}
          child={<PprChipStatus status={multipleMonitorCheckStatus} />}
        />
      )}
    </Flex>
  );
};

export default Screen;
